import { extractQueryState } from "@shared/utils/extractQueryState";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { getSubscriptionApiClient } from "@shared/apis/sidekick/api-factories";
import { GetSubscriptionStatusResponse } from "@shared/apis/sidekick/generated";
import {
  ApiError,
  apiResponseErrorHandler,
} from "@shared/apis/sidekick/api-error-handler";
import { toast } from "react-toastify";

export const buildAdminGetUserSubscriptionStatusQueryKey = ({
  userId,
  forceRefresh,
}: {
  userId: string;
  forceRefresh?: boolean;
}) => {
  return ["admin-user-subscription-status", userId, forceRefresh].filter(
    (item) => item !== undefined
  );
};

type Props = {
  onSuccess?: (data: GetSubscriptionStatusResponse) => void;
  onError?: (error: ApiError) => void;
  userId: string;
  forceRefresh?: boolean;
};

export const useAdminGetUserSubscription = (props: Props) => {
  const { userId, forceRefresh = false } = props;
  const api = getSubscriptionApiClient();
  const query = useQuery({
    queryKey: buildAdminGetUserSubscriptionStatusQueryKey({
      userId,
      forceRefresh,
    }),
    queryFn: async () => {
      try {
        const data = await api.subscriptionControllerGetSubscriptionStatus({
          userId,
          forceRefresh,
        });
        props?.onSuccess?.(data);
        return data;
      } catch (e) {
        console.error(`Error trying to fetch the subscription status`, e);
        const error = await apiResponseErrorHandler(e);
        toast.error(
          `Error trying to fetch the subscription status:  ${error.errors[0]}`
        );
        props?.onError?.(error);
        throw error;
      }
    },
    placeholderData: keepPreviousData,
    enabled: !!userId,
    staleTime: 0,
  });

  return extractQueryState(query);
};
